import React, { useRef, useEffect, useCallback } from 'react';
import { colors2, icons, setVal, deleteVal, UI_TAG, UI_BODY_SM } from 'oolib';
import styled from 'styled-components';
import { useWidgetTitle } from './useWidgetTitle';
import { useAnimatedHeight } from './useAnimatedHeight';

const StyledCard = styled.div`
  background: #F7F7F7;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid transparent;
  transition: border-color 0.15s;
  cursor: text;
  display: flex;
  align-items: stretch;

  &:hover {
    border-color: #C6C6C6;
  }

  &:focus-within {
    background: ${colors2.white};
    border-color: ${colors2.grey80};
    box-shadow: 0 0.4rem 1rem 0 rgba(0, 0, 0, 0.12);
  }
`;

const StyledActionSlot = styled.div`
  display: flex;
  align-items: center;
  color: ${colors2.grey30};

  ${StyledCard}:hover & {
    color: ${colors2.grey80};
  }
`;

const StyledSideAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
  min-width: 34px;
  border: none;
  background: transparent;
  align-self: stretch;

  &:hover {
    background: ${colors2.white};
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const StyledContentSlot = styled.div`
  flex: 1;
  min-width: 0;
  padding: 1rem 1rem 0;
  margin-bottom: 0.1rem;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  overflow: hidden;
  font: inherit;
  color: inherit;
  padding: 0;
  min-height: 21px;
  margin-top:2px;

  &::placeholder {
    color: ${colors2.grey50};
  }
`;

/**
 * Card-styled text input with auto-wrapping, optional tag label, and action button.
 *
 * Two modes:
 * 1. Widget title mode: pass widgetConfig, widgetType, setConfig, underEditWidgetIdx
 *    — auto-generates default title, shows "Switch to Default" action
 * 2. Plain mode: pass id, value, onChange directly
 */
export const TitleInputWithDefault = ({
  id,
  value: valueProp,
  onChange: onChangeProp,
  placeholder = 'Enter title',
  tagLabel = 'WIDGET TITLE',
  onBlur,
  onFocus,
  action: actionProp,
  widgetConfig,
  widgetType,
  setConfig,
  underEditWidgetIdx,
  titlePath = 'props.title',
  hasCustomTitlePath = 'props.hasCustomTitle',
}) => {
  const isWidgetTitleMode = !!(widgetConfig && setConfig);

  const titleHook = useWidgetTitle({ widgetConfig, widgetType, titlePath, hasCustomTitlePath });

  const { displayTitle, shouldShowDefaultButton, generateDefaultTitle } = isWidgetTitleMode
    ? titleHook
    : { displayTitle: null, shouldShowDefaultButton: false, generateDefaultTitle: () => '' };

  // Resolve value & onChange based on mode
  const resolvedValue = isWidgetTitleMode ? displayTitle : valueProp;
  const resolvedId = isWidgetTitleMode ? titlePath : id;

  const handleChange = isWidgetTitleMode
    ? (_, newTitle) => {
        setConfig((prev) => {
          let modifiedConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
          modifiedConfig = setVal(modifiedConfig, titlePath, newTitle);
          modifiedConfig = setVal(modifiedConfig, hasCustomTitlePath, true);
          const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
          modifiedFullConfig[underEditWidgetIdx] = modifiedConfig;
          return modifiedFullConfig;
        });
      }
    : onChangeProp;

  const handleSwitchToDefault = () => {
    setConfig((prev) => {
      let modifiedConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const defaultTitle = generateDefaultTitle();
      modifiedConfig = setVal(modifiedConfig, titlePath, defaultTitle || '');
      modifiedConfig = deleteVal(modifiedConfig, hasCustomTitlePath);
      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[underEditWidgetIdx] = modifiedConfig;
      return modifiedFullConfig;
    });
  };

  const action = isWidgetTitleMode
    ? (shouldShowDefaultButton ? {
        icon: 'ArrowCounterClockwise',
        onClick: handleSwitchToDefault,
      } : undefined)
    : actionProp;

  const cardRef = useRef(null);
  const textareaRef = useRef(null);
  const ActionIcon = action?.icon ? icons[action.icon] : null;
  const wrapperStyle = useAnimatedHeight(cardRef);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);

  useEffect(() => {
    autoResize();
  }, [resolvedValue, autoResize]);

  const handleClick = () => {
    textareaRef.current?.focus();
  };

  return (
    <div style={wrapperStyle}>
      <StyledCard ref={cardRef} onClick={handleClick}>
        <StyledContentSlot>
          {tagLabel && (
            <UI_TAG style={{ color: colors2.grey50 }}>{tagLabel}</UI_TAG>
          )}
          <UI_BODY_SM as="div" style={{ color: colors2.grey80 }}>
            <StyledTextarea
              ref={textareaRef}
              id={resolvedId}
              value={resolvedValue || ''}
              placeholder={placeholder}
              rows={1}
              onChange={(e) => handleChange?.(resolvedId, e.target.value)}
              onBlur={() => onBlur?.()}
              onFocus={() => onFocus?.()}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            />
          </UI_BODY_SM>
        </StyledContentSlot>
        {action && ActionIcon && (
          <StyledActionSlot>
            <StyledSideAction
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.();
              }}
              title={action.tooltip}
            >
              <ActionIcon size={16} weight="bold" />
            </StyledSideAction>
          </StyledActionSlot>
        )}
      </StyledCard>
    </div>
  );
};
